"use client";

import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { Bell, ChevronDown, ChevronUp, Clock3, Heart, LogOut, Menu, MessageCircle, PawPrint, UserRound, X } from "lucide-react";
import { useAppStore } from "@/store/useAppStore";
import { AuthUser, getOptionalMe, logout } from "@/features/auth/api";
import { AuthRequiredLink } from "@/components/AuthGate";
import { getConversationThreads } from "@/features/applications/api";

export function SiteHeader({ compact = false }: { compact?: boolean }) {
  const count = useAppStore((state) => state.favorites.length);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [unreadChats, setUnreadChats] = useState(0);
  const accountMenuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let active = true;
    const sync = async () => {
      try {
        const nextUser = await getOptionalMe();
        if (active) setUser(nextUser);
      } catch {
        if (active) setUser(null);
      }
    };
    void sync();
    window.addEventListener("ondog-auth-change", sync);
    return () => {
      active = false;
      window.removeEventListener("ondog-auth-change", sync);
    };
  }, []);
  useEffect(() => {
    if (!mobileMenuOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMobileMenuOpen(false);
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [mobileMenuOpen]);
  useEffect(() => {
    if (!accountMenuOpen) return;
    const close = (event: MouseEvent) => {
      if (!accountMenuRef.current?.contains(event.target as Node)) setAccountMenuOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setAccountMenuOpen(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [accountMenuOpen]);
  useEffect(() => {
    if (!user) { setUnreadChats(0); return; }
    const syncUnread = () => void getConversationThreads().then(items => setUnreadChats(items.reduce((sum,item)=>sum+item.unreadCount,0))).catch(()=>undefined);
    syncUnread();
    const timer=window.setInterval(syncUnread,15000);
    window.addEventListener("ondog-chat-read",syncUnread);
    return()=>{window.clearInterval(timer);window.removeEventListener("ondog-chat-read",syncUnread)};
  },[user]);
  const accountLinks = user?.roles.includes("ADMIN")
    ? [
        ["운영 콘솔", "/admin"],
        ["콘텐츠 관리", "/admin/content"],
        ["고객 지원", "/admin/support"],
        ["서비스 상태", "/admin/operations"],
        ["분쟁·정산", "/admin/transactions"],
      ]
    : user?.roles.includes("BREEDER")
      ? [
          ["브리더 프로필", "/breeder/profile"],
          ["신청자 관리", "/breeder/applications"],
          ["개체·분양 관리", "/breeder/marketplace"],
          ["검증 자료", "/breeder/verifications"],
          ["CRM·분석", "/breeder/crm"],
        ]
      : [
          ["내 프로필", "/profile"],
          ["신청·주문·결제", "/applications"],
          ["알림", "/notifications"],
          ["도움 요청", "/support"],
          ["브리더 신청", "/breeder/profile"],
        ];
  const signOut = async () => {
    await logout();
    setUser(null);
    setAccountMenuOpen(false);
    setMobileMenuOpen(false);
  };
  return (
    <>
      {!compact && (
        <div className="notice">
          건강 기록과 돌봄 환경을 충분히 확인하고 만나세요.{" "}
          <Link href="/#promise">온독의 기준 보기</Link>
        </div>
      )}
      <header className={`site-header ${compact ? "compact" : ""}`}>
        <div className="nav shell" aria-label="주요 메뉴">
          <Link className="brand" href="/" aria-label="온독 홈">
            <span className="brand-mark">
              <PawPrint size={19} />
            </span>
            <span>
              온독<small>따뜻한 분양의 온도</small>
            </span>
          </Link>
          <nav>
            <Link href="/dogs">강아지 찾기</Link>
            <Link href="/breeds">견종 가이드</Link>
            <Link href="/breed-match">나에게 맞는 견종</Link>
            {/*<Link href="/guides">전문가 가이드</Link>
            <Link href="/waitlist">소식 받기</Link>
            <Link href="/applications">신청 내역</Link>
            <Link href="/orders">주문·결제</Link>
            <Link href="/reviews">후기</Link>
            <Link href="/notifications">웹 알림</Link>
            <Link href="/support">도움 요청</Link>*/}
            <Link href="/#promise">책임 분양 기준</Link>
          </nav>
          <div className="nav-actions">
            <Link
              className="utility-link recent-link"
              href="/recent"
              aria-label="최근 본 친구"
            >
              <Clock3 size={18} />
            </Link>
            <AuthRequiredLink
              className="favorite-link"
              href="/favorites"
              message="찜한 친구를 확인하려면 로그인이 필요합니다."
            >
              <Heart size={18} />
              {count > 0 && <span>{count}</span>}
            </AuthRequiredLink>
            {user ? (
              <div className="account-menu-wrap" ref={accountMenuRef}>
                <button className="account-trigger" type="button" aria-haspopup="menu" aria-expanded={accountMenuOpen} onClick={() => setAccountMenuOpen((open) => !open)}>
                  <span className="account-avatar"><UserRound /></span>
                  <span>{user.name}</span>
                  {unreadChats > 0 && (
                    <span className="account-message-alert" aria-label={`읽지 않은 메시지 ${unreadChats}개`} title={`읽지 않은 메시지 ${unreadChats}개`}>
                      <Bell aria-hidden="true" />
                      <b>{unreadChats > 99 ? "99+" : unreadChats}</b>
                    </span>
                  )}
                  {accountMenuOpen ? <ChevronUp className="account-chevron" /> : <ChevronDown className="account-chevron" />}
                </button>
                {accountMenuOpen && <div className="account-dropdown" role="menu">
                  <div className="account-summary"><b>{user.name}</b><small>{user.userId}</small></div>
                  <div className="account-links"><Link href="/chats" role="menuitem" className="chat-menu-link" onClick={() => setAccountMenuOpen(false)}><MessageCircle/>채팅{unreadChats>0&&<span className="chat-unread">{unreadChats>99?"99+":unreadChats}</span>}</Link>{accountLinks.map(([label, href]) => <Link key={href} href={href} role="menuitem" onClick={() => setAccountMenuOpen(false)}>{label}</Link>)}</div>
                  <button type="button" className="account-logout" role="menuitem" onClick={() => void signOut()}><LogOut />로그아웃</button>
                </div>}
              </div>
            ) : (
              <div className="auth-entry-links">
                <Link className="outline-btn" href="/auth">로그인</Link>
                <Link className="outline-btn signup-entry" href="/signup">회원가입</Link>
              </div>
            )}
            <button className="menu" type="button" aria-label="메뉴 열기" aria-expanded={mobileMenuOpen} aria-controls="mobile-navigation" onClick={() => setMobileMenuOpen(true)}>
              <Menu />
            </button>
          </div>
        </div>
      </header>
      {/* Pointer convenience only; Escape and the close button remain keyboard paths. */}
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      {mobileMenuOpen && <div className="mobile-nav-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) setMobileMenuOpen(false); }}>
        <nav id="mobile-navigation" className="mobile-nav" aria-label="모바일 메뉴">
          <div><b>메뉴</b><button type="button" onClick={() => setMobileMenuOpen(false)} aria-label="메뉴 닫기"><X /></button></div>
          <Link href="/dogs" onClick={() => setMobileMenuOpen(false)}>강아지 찾기</Link>
          <Link href="/breeds" onClick={() => setMobileMenuOpen(false)}>견종 가이드</Link>
          <Link href="/breed-match" onClick={() => setMobileMenuOpen(false)}>나에게 맞는 견종</Link>
          <Link href="/#promise" onClick={() => setMobileMenuOpen(false)}>책임 분양 기준</Link>
          <Link href="/recent" onClick={() => setMobileMenuOpen(false)}>최근 본 친구</Link>
          <AuthRequiredLink href="/favorites" className="mobile-auth-link" message="찜한 친구를 확인하려면 로그인이 필요합니다.">찜한 친구</AuthRequiredLink>
          {user && <><div className="mobile-account-summary"><UserRound/><span><b>{user.name}</b><small>{user.userId}</small></span></div><Link href="/chats" className="mobile-chat-link" onClick={() => setMobileMenuOpen(false)}><MessageCircle/>채팅{unreadChats>0&&<span className="chat-unread">{unreadChats>99?"99+":unreadChats}</span>}</Link>{accountLinks.map(([label, href]) => <Link key={href} href={href} onClick={() => setMobileMenuOpen(false)}>{label}</Link>)}<button type="button" className="mobile-logout" onClick={() => void signOut()}><LogOut/>로그아웃</button></>}
          {!user && <><Link className="mobile-login" href="/auth" onClick={() => setMobileMenuOpen(false)}>로그인</Link><Link className="mobile-login" href="/signup" onClick={() => setMobileMenuOpen(false)}>회원가입</Link></>}
        </nav>
      </div>}
    </>
  );
}
